// src/core/ExpertInfoForm.js
import React, { useState } from 'react';
import { toPersianDigits, provinces, degrees, toEnglishDigits } from './utils';

const ExpertInfoForm = ({ expertInfo, onExpertInfoChange, nationalId, onSaveExpertInfo, isNewUser }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverErrors, setServerErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    if (!expertInfo.firstName.trim()) newErrors.firstName = 'نام نمی‌تواند خالی باشد.';
    if (!expertInfo.lastName.trim()) newErrors.lastName = 'نام خانوادگی نمی‌تواند خالی باشد.';
    if (!expertInfo.province) newErrors.province = 'انتخاب استان اجباری است.';
    if (!expertInfo.position.trim()) newErrors.position = 'سمت نمی‌تواند خالی باشد.';
    if (!expertInfo.specialties.trim()) newErrors.specialties = 'مهارت‌ها و تخصص‌ها نمی‌تواند خالی باشد.';
    if (!expertInfo.degree) newErrors.degree = 'انتخاب مدرک تحصیلی اجباری است.';
    if (!expertInfo.education.trim()) newErrors.education = 'رشته تحصیلی نمی‌تواند خالی باشد.';

    if (!expertInfo.email.trim()) {
      newErrors.email = 'ایمیل نمی‌تواند خالی باشد.';
    } else if (!/\S+@\S+\.\S+/.test(expertInfo.email)) {
      newErrors.email = 'فرمت ایمیل نامعتبر است.';
    }

    const englishMobileNumber = toEnglishDigits(expertInfo.mobileNumber);
    if (!englishMobileNumber.trim()) {
      newErrors.mobileNumber = 'شماره همراه نمی‌تواند خالی باشد.';
    } else if (!/^09\d{9}$/.test(englishMobileNumber)) {
      newErrors.mobileNumber = 'شماره همراه باید ۱۱ رقمی و با ۰۹ شروع شود.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    setServerErrors({});
    if (!validate()) {
      return;
    }
    setIsSaving(true);
    const expertInfoToSend = {
      ...expertInfo,
      mobileNumber: toEnglishDigits(expertInfo.mobileNumber),
      nationalId: toEnglishDigits(nationalId)
    };
    const result = await onSaveExpertInfo(expertInfoToSend);
    if (result && result.success === false) {
      setServerErrors(result.errors || {});
    }
    setIsSaving(false);
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-blue-700 text-center">اطلاعات کارشناس</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">نام</label>
          <input
            type="text" id="firstName" name="firstName"
            value={expertInfo.firstName} onChange={onExpertInfoChange}
            className={`w-full p-3 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right ${errors.firstName || serverErrors.firstName ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="نام خود را وارد کنید"
          />
          {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
          {serverErrors.firstName && <p className="text-red-500 text-xs mt-1">{serverErrors.firstName}</p>}
        </div>
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">نام خانوادگی</label>
          <input
            type="text" id="lastName" name="lastName"
            value={expertInfo.lastName} onChange={onExpertInfoChange}
            className={`w-full p-3 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right ${errors.lastName || serverErrors.lastName ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="نام خانوادگی خود را وارد کنید"
          />
          {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
          {serverErrors.lastName && <p className="text-red-500 text-xs mt-1">{serverErrors.lastName}</p>}
        </div>

        <div>
          <label htmlFor="nationalId" className="block text-sm font-medium text-gray-700 mb-1">کد ملی</label>
          <input
            type="text" id="nationalId" name="nationalId"
            value={toPersianDigits(nationalId)} readOnly
            className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-right"
          />
          {serverErrors.nationalId && <p className="text-red-500 text-xs mt-1">{serverErrors.nationalId}</p>}
        </div>
        <div>
          <label htmlFor="province" className="block text-sm font-medium text-gray-700 mb-1">استان</label>
          <select
            id="province" name="province"
            value={expertInfo.province} onChange={onExpertInfoChange}
            className={`w-full p-3 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right border-gray-300 bg-white ${errors.province || serverErrors.province ? 'border-red-500' : 'border-gray-300'}`}
          >
            <option value="">انتخاب کنید...</option>
            {provinces.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {errors.province && <p className="text-red-500 text-xs mt-1">{errors.province}</p>}
          {serverErrors.province && <p className="text-red-500 text-xs mt-1">{serverErrors.province}</p>}
        </div>

        <div>
          <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1">سمت</label>
          <input
            type="text" id="position" name="position"
            value={expertInfo.position} onChange={onExpertInfoChange}
            className={`w-full p-3 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right ${errors.position || serverErrors.position ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="مثال: کارشناس پرتوشناسی/مسئول امور اداری/معاون بیمه‌ای/رئیس شعبه/مدیر درمان"
          />
          {errors.position && <p className="text-red-500 text-xs mt-1">{errors.position}</p>}
          {serverErrors.position && <p className="text-red-500 text-xs mt-1">{serverErrors.position}</p>}
        </div>
        <div>
          <label htmlFor="specialties" className="block text-sm font-medium text-gray-700 mb-1">مهارت‌ها و تخصص‌ها (غیر از حوزه‌ی کاری)</label>
          <input
            type="text" id="specialties" name="specialties"
            value={expertInfo.specialties} onChange={onExpertInfoChange}
            className={`w-full p-3 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right ${errors.specialties || serverErrors.specialties ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="مثال: ترجمه/تحلیل مالی/داده‌کاوی/حسابداری/نرم‌افزار خاص/برنامه‌نویسی"
          />
          {errors.specialties && <p className="text-red-500 text-xs mt-1">{errors.specialties}</p>}
          {serverErrors.specialties && <p className="text-red-500 text-xs mt-1">{serverErrors.specialties}</p>}
        </div>

        <div>
          <label htmlFor="degree" className="block text-sm font-medium text-gray-700 mb-1">مدرک تحصیلی</label>
          <select
            id="degree" name="degree"
            value={expertInfo.degree} onChange={onExpertInfoChange}
            className={`w-full p-3 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right border-gray-300 bg-white ${errors.degree || serverErrors.degree ? 'border-red-500' : 'border-gray-300'}`}
          >
            <option value="">انتخاب کنید...</option>
            {degrees.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          {errors.degree && <p className="text-red-500 text-xs mt-1">{errors.degree}</p>}
          {serverErrors.degree && <p className="text-red-500 text-xs mt-1">{serverErrors.degree}</p>}
        </div>
        <div>
          <label htmlFor="education" className="block text-sm font-medium text-gray-700 mb-1">رشته تحصیلی</label>
          <input
            type="text" id="education" name="education"
            value={expertInfo.education} onChange={onExpertInfoChange}
            className={`w-full p-3 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right ${errors.education || serverErrors.education ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="مثال: پرستاری/مدیریت صنایع/فناوری اطلاعات/جامعه شناسی"
          />
          {errors.education && <p className="text-red-500 text-xs mt-1">{errors.education}</p>}
          {serverErrors.education && <p className="text-red-500 text-xs mt-1">{serverErrors.education}</p>}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">ایمیل</label>
          <input
            type="email" id="email" name="email"
            value={expertInfo.email} onChange={onExpertInfoChange}
            className={`w-full p-3 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right ${errors.email || serverErrors.email ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="abcd@efg.com"
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          {serverErrors.email && <p className="text-red-500 text-xs mt-1">{serverErrors.email}</p>}
        </div>
        <div>
          <label htmlFor="mobileNumber" className="block text-sm font-medium text-gray-700 mb-1">شماره همراه</label>
          <input
            type="tel" id="mobileNumber" name="mobileNumber"
            value={toPersianDigits(expertInfo.mobileNumber)} onChange={onExpertInfoChange}
            className={`w-full p-3 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right ${errors.mobileNumber || serverErrors.mobileNumber ? 'border-red-500' : 'border-gray-300'}`}
            maxLength="11"
            placeholder="۰۹۱۲۳۴۵۶۷۸۹"
          />
          {errors.mobileNumber && <p className="text-red-500 text-xs mt-1">{errors.mobileNumber}</p>}
          {serverErrors.mobileNumber && <p className="text-red-500 text-xs mt-1">{serverErrors.mobileNumber}</p>}
        </div>
      </div>
      {serverErrors.general && (
        <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center">
          {serverErrors.general}
        </div>
      )}
      <div className="mt-8 text-center">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 px-8 rounded-lg shadow-lg transition duration-300 ease-in-out transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'در حال ذخیره...' : 'ذخیره اطلاعات کارشناس'}
        </button>
      </div>
    </div>
  );
};

export default ExpertInfoForm;
